import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Database, MapPin, FileText, TrendUp, ArrowsLeftRight } from "@phosphor-icons/react";

function Stat({ label, value, accent, testid }) {
    return (
        <div className="border border-gray-200 bg-white rounded-lg p-3" data-testid={testid}>
            <div className={`font-display-black text-2xl tabular-nums ${accent || ""}`}>{value}</div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}

const SOURCES = [
    { key: "FALLCO", label: "FallcoAste" },
    { key: "ASTEGIUDIZIARIE", label: "AsteGiudiziarie" },
    { key: "AVVISI_NOTARILI", label: "Avvisi Notarili (RAN)" },
    { key: "PVP_REAL", label: "PVP" },
];

export function SourceAudit() {
    const [source, setSource] = useState("FALLCO");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (src) => {
        setLoading(true);
        try {
            const res = await api.get("/admin/sources/audit", { params: { source: src } });
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(source); }, [load, source]);

    const s = data?.summary;
    const g = data?.geography;
    const d = data?.documents;
    const k = data?.kpi_impact_exclusive;
    const cs = data?.crawl_state;

    return (
        <div className="mb-8" data-testid="source-audit">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Database size={16} weight="bold" />
                    <h2 className="font-display-black text-lg">Audit fonte · contributo alla copertura</h2>
                </div>
                <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    data-testid="source-audit-select"
                    className="border border-gray-300 h-9 px-2 text-sm focus:border-black outline-none w-full sm:w-auto"
                >
                    {SOURCES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="border border-gray-200 bg-white rounded-xl p-5 text-sm text-gray-500" data-testid="source-audit-loading">
                    Calcolo audit fonte…
                </div>
            ) : !data ? null : (
                <div className="border border-gray-200 bg-white rounded-xl shadow-card p-5" data-testid="source-audit-content">
                    {/* Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Stat label="Aste viste dalla fonte" value={s.touched} testid="sa-touched" />
                        <Stat label="Nuove nette (esclusive)" value={s.net_new_exclusive} accent="text-emerald-600" testid="sa-netnew" />
                        <Stat label="Overlap AsteGiudiziarie" value={s.overlap_with_astegiudiziarie} testid="sa-overlap" />
                        <Stat label="Quota del master" value={`${s.share_of_master_pct}%`} testid="sa-share" />
                    </div>

                    {/* Geography */}
                    <div className="flex items-center gap-2 mb-2 mt-2">
                        <MapPin size={14} weight="bold" /><span className="overline">Copertura geografica</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <Stat label="Province coperte" value={g.provinces_covered_count} testid="sa-prov-covered" />
                        <Stat label="Province esclusive" value={g.provinces_exclusive_count} accent="text-indigo-600" testid="sa-prov-excl" />
                        <Stat label="Regioni coperte" value={g.regions_covered.length} testid="sa-regions" />
                        <Stat label="Regioni esclusive" value={g.regions_exclusive.length} accent="text-indigo-600" testid="sa-regions-excl" />
                    </div>
                    {g.regions_covered.length > 0 && (
                        <div className="text-xs text-gray-500 mb-4" data-testid="sa-regions-list">
                            Regioni: {g.regions_covered.join(" · ")}
                        </div>
                    )}
                    {g.top_provinces_net_new.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4" data-testid="sa-top-provinces">
                            {g.top_provinces_net_new.map((p) => (
                                <span key={p.province} className="text-xs border border-gray-200 rounded-full px-2.5 py-1 bg-gray-50">
                                    {p.province} <span className="font-bold tabular-nums">{p.count}</span>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Documents */}
                    <div className="flex items-center gap-2 mb-2">
                        <FileText size={14} weight="bold" /><span className="overline">Documenti & deep_ready</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <Stat label="Perizie disponibili" value={d.perizie_available} testid="sa-perizie" />
                        <Stat label="Deep ready (ora)" value={d.deep_ready_now} accent="text-indigo-600" testid="sa-deep-now" />
                        <Stat label="Deep ready potenziali" value={d.deep_ready_potential} accent="text-amber-600" testid="sa-deep-pot" />
                    </div>

                    {/* KPI impact (exclusive contribution) */}
                    <div className="flex items-center gap-2 mb-2">
                        <TrendUp size={14} weight="bold" /><span className="overline">Impatto KPI nazionali (contributo esclusivo)</span>
                    </div>
                    <div className="overflow-x-auto mb-2" data-testid="sa-kpi-table">
                        <table className="w-full text-sm min-w-[420px]">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="py-2 pr-3 font-bold">KPI</th>
                                    <th className="py-2 px-3 font-bold text-right">Da questa fonte</th>
                                    <th className="py-2 px-3 font-bold text-right">Nazionale</th>
                                    <th className="py-2 pl-3 font-bold text-right">Quota</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[["Perizie", k.perizie], ["Deep ready", k.deep_ready], ["Superfici", k.surfaces], ["€/m²", k.price_sqm]].map(([label, v]) => (
                                    <tr key={label} className="border-b border-gray-100" data-testid={`sa-kpi-${label}`}>
                                        <td className="py-2 pr-3 font-semibold">{label}</td>
                                        <td className="py-2 px-3 text-right tabular-nums font-bold">{v.source}</td>
                                        <td className="py-2 px-3 text-right tabular-nums text-gray-500">{v.national}</td>
                                        <td className="py-2 pl-3 text-right tabular-nums text-emerald-600 font-bold">{v.share_pct}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-start gap-2 text-xs text-gray-400 mt-3">
                        <ArrowsLeftRight size={13} weight="bold" className="mt-0.5 shrink-0" />
                        <span>
                            L'overlap è una stima <b>prudenziale</b> (richiede stessa chiave Tribunale|RGE tra fonti).
                            {cs ? ` · Crawler: offset ${cs.offset}/${cs.total_candidates}, ultimo run ha tenuto ${cs.last_kept} su ${cs.last_scanned} scansionate.` : ""}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
